import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import axios, { type AxiosRequestConfig, AxiosResponse } from 'axios';
import { fixed } from 'innovate/utils/Constants';
import {
  ApprovedUser,
  Flavour,
  ScenarioType,
  type DistributionTemplateRequest,
  type DistributionTemplateResponse,
} from 'innovate/utils/types';
import {
  SurveyStatus,
  SurveySubmitPayload,
  SurveySubmitResponse,
} from 'innovate/utils/surveyTypes';
import {
  ApprovedUser as AdminApprovedUser,
  PendingApprovalData,
  AdminScenario,
  AdminSimulationStats,
  AdminSimulationFilterOptions,
  PaginatedScenariosResponse,
  AdminSimulationsParams,
  AdminSimulationCharts,
  SimulationMutationResponse,
  DashboardOverviewResponse,
} from 'innovate/pages/AdminPages/types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AUTH_TOKEN_KEY = 'Auth_token';
export type Response<T> =
  | {
      simulation: boolean;
      success: true;
      data: T;
    }
  | { success: false; err?: AxiosResponse };

type AuthResponse = { token_type: string; access_token: string };
type NavigateFunction = ReturnType<typeof useNavigate>;
type ResponseData = {
  is_admin: boolean;
  success: boolean;
  data?: {
    is_admin?: boolean;
  };
};
type scenarioData = {
  simulations: ScenarioType[];
  success: boolean;
};
interface Market {
  country_code: string;
  country_name: string;
}
type ApprovedCountriesResponse = {
  markets: Market[];
  success: boolean;
  data: {
    markets: Market[];
  };
};

export interface AvailableCountry {
  country_code: string;
  country_name: string;
  model: string;
  m_name: string;
}

type AvailableCountriesResponse = AvailableCountry[];

export interface SimulationNamesResponse {
  simulations: Flavour[];
  success: boolean;
  data: {
    simulations: Flavour[];
  };
}

// Helper: Clear auth data and navigate to 404
const clearAuthAndNavigate = (navigate: NavigateFunction, status: string = '401') => {
  localStorage.clear();
  sessionStorage.clear();
  navigate('/404', {
    state: { apiEndpoint: `${fixed}users/sso-login`, status },
  });
};

// Helper: Call SSO login endpoint once
const getMsalCountry = async (accessToken?: string): Promise<string | null> => {
  if (!accessToken) return null;

  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=country', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) return null;

    const userData = (await response.json()) as { country?: string | null };
    return userData.country ?? null;
  } catch (error) {
    console.error('Microsoft Graph country lookup failed:', error);
    return null;
  }
};

const callSSOLogin = async (authResponse: any): Promise<void> => {
  const hasCalledSSO = localStorage.getItem(AUTH_TOKEN_KEY);
  if (hasCalledSSO) return;

  try {
    const graphCountry = await getMsalCountry(authResponse.accessToken);
    const msalCountry =
      graphCountry ??
      authResponse.account?.idTokenClaims?.country ??
      authResponse.account?.idTokenClaims?.ctry ??
      null;

    const ssoResponse = await axios<AuthResponse>({
      url: `${fixed}users/sso-login`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authResponse.idToken}`,
      },
      data: {
        ...authResponse.account,
        msal_country: msalCountry,
      },
    });

    if (ssoResponse.status === 200) {
      localStorage.setItem(AUTH_TOKEN_KEY, 'called');
    }
  } catch (ssoError) {
    console.error('SSO login error:', ssoError);
    localStorage.setItem(AUTH_TOKEN_KEY, 'called');
  }
};

// Helper: Handle interaction required error
const handleInteractionRequired = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<null> => {
  try {
    await msalDetails.instance.acquireTokenRedirect({
      scopes: ['user.read', 'openid', 'profile', 'email'],
      account: msalDetails.accounts[0],
    });
  } catch (redirectError) {
    console.error('Error during token redirect:', redirectError);
    clearAuthAndNavigate(navigate, '401');
  }
  return null;
};

export const getAuthToken = async (msalDetails: IMsalContext, navigate: NavigateFunction) => {
  try {
    if (!msalDetails.accounts || msalDetails.accounts.length === 0) {
      console.error('No MSAL accounts available');
      toast.error('Authentication error. Please log in again.');
      navigate('/');
      return null;
    }

    const authResponse = await msalDetails.instance.acquireTokenSilent({
      scopes: ['user.read', 'openid', 'profile', 'email'],
      account: msalDetails.accounts[0],
    });

    if (!authResponse?.idToken) {
      clearAuthAndNavigate(navigate, '401');
      return null;
    }

    // Call SSO login once for user creation/lookup
    await callSSOLogin(authResponse);

    // ALWAYS return the fresh MSAL idToken
    return `Bearer ${authResponse.idToken}`;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      return handleInteractionRequired(msalDetails, navigate);
    }

    // Handle other errors
    const status = (err as any).response?.status || '500';
    clearAuthAndNavigate(navigate, status);
    console.debug('error in getting token', err);
    return null;
  }
};

export const useIsAuthorized = () => {
  const msalDetails = useMsal();
  const navigate = useNavigate();
  useEffect(() => {
    getAuthToken(msalDetails, navigate).catch((err) => {
      console.debug(err);
    });
  }, [msalDetails, navigate]);
};

// Helper: Handle session expiry with delayed redirect
const handleSessionExpiry = (navigate: NavigateFunction): Response<any> => {
  toast.error('Session expired. Please log in again.');
  setTimeout(() => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  }, 2000);
  return { success: false };
};

// Helper: Refresh token and retry request
const refreshTokenAndRetry = async <T>(
  headerConfig: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<Response<T>> => {
  localStorage.removeItem(AUTH_TOKEN_KEY);

  try {
    const authResponse = await msalDetails.instance.acquireTokenSilent({
      scopes: ['user.read', 'openid', 'profile', 'email'],
      account: msalDetails.accounts[0],
      forceRefresh: true,
    });

    if (!authResponse?.idToken) {
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
      return { success: false };
    }

    const token = `Bearer ${authResponse.idToken}`;
    if (headerConfig.headers) {
      headerConfig.headers.Authorization = token;
    }

    const retryResponse = await axios<T>(headerConfig);
    return {
      success: true,
      data: retryResponse.data,
    } as Response<T>;
  } catch (silentError) {
    console.error('Error during silent token acquisition:', silentError);

    if (silentError instanceof InteractionRequiredAuthError) {
      toast.info('Your session has expired. Redirecting to login...');
      try {
        await msalDetails.instance.acquireTokenRedirect({
          scopes: ['user.read', 'openid', 'profile', 'email'],
          account: msalDetails.accounts[0],
        });
        return { success: false };
      } catch (redirectError) {
        console.error('Interactive login failed:', redirectError);
      }
    }

    return handleSessionExpiry(navigate);
  }
};

// Helper: Handle 401 authentication errors
const handle401Error = async <T>(
  err: any,
  headerConfig: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<Response<T>> => {
  console.error('Got 401, refreshing token...', err.response?.data);
  return refreshTokenAndRetry<T>(headerConfig, msalDetails, navigate);
};

// Helper: Handle axios errors
const handleAxiosError = async <T>(
  err: any,
  headerConfig: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<Response<T>> => {
  if (err.response?.status === 401) {
    return handle401Error<T>(err, headerConfig, msalDetails, navigate);
  }

  // Other HTTP errors - don't log user out
  console.error('Error in httpCall:', err);
  return { success: false, err: err.response };
};

// Helper: Handle network/CORS errors
const handleNetworkError = <T>(err: any): Response<T> => {
  console.error('Network or CORS error:', err.message);
  toast.warning('Network connection issue. Please check your internet and try again.');
  return {
    success: false,
    err: { message: err.message, code: err.code } as any,
  };
};

const httpCall = async <T>(
  config: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<Response<T>> => {
  const token = await getAuthToken(msalDetails, navigate);
  if (!token) {
    return { success: false };
  }

  const headerConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      ...config.headers,
      Authorization: token,
      'Cache-Control': 'no-store,no-cache,',
      Pragma: 'no-cache',
    },
  };

  return axios<T>(headerConfig)
    .then((response) => {
      return {
        success: true,
        data: response.data,
      } as Response<T>;
    })
    .catch(async (err) => {
      if (axios.isAxiosError(err)) {
        return handleAxiosError<T>(err, headerConfig, msalDetails, navigate);
      }
      return handleNetworkError<T>(err);
    });
};

// SSE (Server-Sent Events) streaming response types
export interface SSEMessage<T = unknown> {
  status: 'processing' | 'completed' | 'failed';
  message?: string;
  result?: T;
  error?: string;
  progress?: number;
}

export interface SSECallbacks<T> {
  onMessage?: (data: SSEMessage<T>) => void;
  onProcessing?: (message?: string, progress?: number) => void;
  onCompleted?: (result: T) => void;
  onFailed?: (error: string) => void;
  onError?: (error: Error) => void;
}

// Helper: Process SSE message callbacks
const processSSEMessage = <T>(parsedData: SSEMessage<T>, callbacks: SSECallbacks<T>): void => {
  callbacks.onMessage?.(parsedData);

  if (parsedData.status === 'processing') {
    callbacks.onProcessing?.(parsedData.message, parsedData.progress);
  } else if (parsedData.status === 'completed' && parsedData.result) {
    callbacks.onCompleted?.(parsedData.result);
  } else if (parsedData.status === 'failed') {
    callbacks.onFailed?.(parsedData.error || 'An error occurred');
  }
};

// Helper: Read and process SSE stream
const processSSEStream = async <T>(
  response: globalThis.Response,
  callbacks: SSECallbacks<T>
): Promise<void> => {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error('No response body');
  }

  let buffer = '';
  let result = await reader.read();

  while (!result.done) {
    buffer += decoder.decode(result.value, { stream: true });

    const messages = buffer.split('\n\n');
    buffer = messages.pop() || '';

    for (const message of messages) {
      if (message.startsWith('data: ')) {
        const jsonData = message.slice(6);
        try {
          const parsedData = JSON.parse(jsonData) as SSEMessage<T>;
          processSSEMessage(parsedData, callbacks);
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
          callbacks.onError?.(parseError as Error);
        }
      }
    }

    // eslint-disable-next-line no-await-in-loop -- stream chunks must be read sequentially
    result = await reader.read();
  }
};

// Helper: Refresh token and retry SSE request
const refreshTokenAndRetrySSE = async (
  url: string,
  data: unknown,
  msalDetails: IMsalContext
): Promise<globalThis.Response> => {
  localStorage.removeItem(AUTH_TOKEN_KEY);

  const authResponse = await msalDetails.instance.acquireTokenSilent({
    scopes: ['user.read', 'openid', 'profile', 'email'],
    account: msalDetails.accounts[0],
    forceRefresh: true,
  });

  console.debug('Token refreshed, retrying SSE...');

  if (!authResponse?.idToken) {
    throw new Error('No token received after refresh');
  }

  const newToken = `Bearer ${authResponse.idToken}`;
  const retryResponse = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: newToken,
      'Cache-Control': 'no-store,no-cache',
      Pragma: 'no-cache',
    },
    body: JSON.stringify(data),
  });

  if (!retryResponse.ok) {
    throw new Error(`Retry failed with status ${retryResponse.status}`);
  }

  return retryResponse;
};

// Helper: Handle SSE 401 errors with token refresh
const handleSSE401Error = async (
  url: string,
  data: unknown,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<globalThis.Response> => {
  try {
    return await refreshTokenAndRetrySSE(url, data, msalDetails);
  } catch (refreshError) {
    console.error('Token refresh failed:', refreshError);

    if (refreshError instanceof InteractionRequiredAuthError) {
      toast.info('Your session has expired. Redirecting to login...');
      try {
        await msalDetails.instance.acquireTokenRedirect({
          scopes: ['user.read', 'openid', 'profile', 'email'],
          account: msalDetails.accounts[0],
        });
      } catch (redirectError) {
        console.error('Interactive login failed:', redirectError);
      }
    }

    toast.error('Session expired. Please log in again.');
    setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
    }, 2000);
    throw new Error('Unauthorized - please log in again');
  }
};

/**
 * Makes an authenticated API call with Server-Sent Events (SSE) streaming support.
 * This is useful for long-running operations that need to send progress updates.
 *
 * Why not axios? Axios doesn't support streaming responses well. We need:
 * 1. ReadableStream support for SSE
 * 2. Real-time progressive data reading
 * 3. Event-based message parsing
 *
 * @param url - The API endpoint URL
 * @param data - The request body data
 * @param callbacks - Callback functions for different SSE events
 * @param msalDetails - MSAL context for authentication
 * @param navigate - Navigation function for error redirects
 * @returns Promise that resolves when the stream completes
 */
export const sseAuthenticatedCall = async <T = unknown>(
  url: string,
  data: unknown,
  callbacks: SSECallbacks<T>,
  msalDetails: IMsalContext,
  navigate: NavigateFunction
): Promise<void> => {
  const token = await getAuthToken(msalDetails, navigate);

  if (!token) {
    const error = new Error('Authentication required');
    callbacks.onError?.(error);
    callbacks.onFailed?.('Authentication required');
    return;
  }

  try {
    let response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: token,
        'Cache-Control': 'no-store,no-cache',
        Pragma: 'no-cache',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const errorBody = await response
          .clone()
          .json()
          .catch(() => ({}));
        console.error('Got 401 on SSE:', url, errorBody);
        response = await handleSSE401Error(url, data, msalDetails, navigate);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    await processSSEStream<T>(response, callbacks);
  } catch (error) {
    console.error('SSE streaming error:', error);
    callbacks.onError?.(error as Error);
    callbacks.onFailed?.((error as Error).message || 'An error occurred');
  }
};

/**
 * React hook for SSE authenticated calls with automatic MSAL integration
 *
 * @example
 * const { callSSE, isLoading } = useSSEAuthenticatedCall();
 *
 * const handleApply = async () => {
 *   await callSSE('/api/filter-data', filterData, {
 *     onProcessing: (msg) => console.debug(msg),
 *     onCompleted: (result) => setData(result),
 *     onFailed: (error) => showToast(error, 'error')
 *   });
 * };
 */
export const useSSEAuthenticatedCall = () => {
  const msalDetails = useMsal();
  const navigate = useNavigate();

  const callSSE = async <T = unknown>(
    url: string,
    data: unknown,
    callbacks: SSECallbacks<T>
  ): Promise<void> => {
    return sseAuthenticatedCall(url, data, callbacks, msalDetails, navigate);
  };

  return { callSSE };
};

export const useAuthenticatedQuery = <T>(
  queryKey: unknown[],
  queryConfig: AxiosRequestConfig,
  options?: {
    refetchOnWindowFocus?: boolean;
    enabled?: boolean;
    staleTime?: number;
  }
): UseQueryResult<Response<T>> => {
  const msalDetails = useMsal();
  const navigate = useNavigate();
  const result = useQuery({
    queryKey,
    queryFn: () => httpCall<T>(queryConfig, msalDetails, navigate),
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    retry: false,
    enabled: options?.enabled ?? true, // Only run the query if enabled is true
    ...(options?.staleTime === undefined ? {} : { staleTime: options.staleTime }),
  });
  const { isPending, error, data } = result;
  useEffect(() => {
    if (error) {
      // Don't navigate on query errors - they're already handled by httpCall
      // Only log the error for debugging
      console.error('Query error:', error);
    }
  }, [error, isPending, data, queryKey]);
  return result;
};

export const useAuthenticatedMutation = <T, MutationData>(
  mutationKey: unknown[],
  getMutationConfig: (data: MutationData) => AxiosRequestConfig
): UseMutationResult<Response<T>, unknown, MutationData, unknown> => {
  const msalDetails = useMsal();
  const navigate = useNavigate();
  const result = useMutation<Response<T>, unknown, MutationData, unknown>({
    mutationKey,
    mutationFn: (data: MutationData) => httpCall<T>(getMutationConfig(data), msalDetails, navigate),
    retry: false,
  });
  const { isIdle, error, data } = result;
  useEffect(() => {
    if (error) {
      // navigate("/")
      console.error('error');
    }
  }, [error, isIdle, data, mutationKey]);
  return result;
};

export const useAdminAuthorized = () => {
  const { data, isLoading } = useAuthenticatedQuery<ResponseData>(['getUser'], {
    url: `${fixed}users/is_admin`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const isAdmin = data?.success ? data.data?.is_admin : undefined;
  return { isAdmin, isLoading };
};

export const SURVEY_STATUS_QUERY_KEY = ['surveyStatus'];

// Survey status is decided by the backend; fetched once per session (no polling)
export const useSurveyStatus = (enabled = true) =>
  useAuthenticatedQuery<SurveyStatus>(
    SURVEY_STATUS_QUERY_KEY,
    {
      url: `${fixed}survey/status`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { enabled, staleTime: Infinity, refetchOnWindowFocus: false }
  );

export const useSubmitSurvey = () =>
  useAuthenticatedMutation<SurveySubmitResponse, SurveySubmitPayload>(['submitSurvey'], (data) => ({
    url: `${fixed}survey/submit`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  }));

export const useSnoozeSurvey = () =>
  useAuthenticatedMutation<SurveyStatus, void>(['snoozeSurvey'], () => ({
    url: `${fixed}survey/snooze`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  }));

// Reusable hook for fetching distribution data
export const useFetchDistributionData = <ResponseType = any, RequestType = any>() => {
  return useAuthenticatedMutation<ResponseType, RequestType>(
    ['fetchDistributionData'],
    (data: RequestType) => ({
      url: `${fixed}fetch_distribution_data`,
      method: 'POST',
      data,
    })
  );
};

// Fetch on demand when downloading a regional template for any market.
export const useFetchDistributionTemplateData = () =>
  useAuthenticatedMutation<DistributionTemplateResponse, DistributionTemplateRequest>(
    ['fetchDistributionTemplateData'],
    (data) => ({
      url: `${fixed}fetch_distribution_template_data`,
      method: 'POST',
      data,
    })
  );

export const useScenarios = (refetchKey: boolean | number = 0) => {
  const location = useLocation();

  const { data, isLoading, isError } = useAuthenticatedQuery<scenarioData>(
    ['getScenarios', refetchKey, location?.key],
    {
      url: `${fixed}simulations`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const fetchedScenarios = data?.success ? data.data.simulations : [];

  return {
    fetchedScenarios,
    isLoading,
    isError,
  };
};
export const useSharedScenarios = (refetchKey: boolean | number = 0) => {
  const location = useLocation();
  const { data, isLoading } = useAuthenticatedQuery<scenarioData>(
    ['getSharedScenarios', refetchKey, location?.key],
    {
      url: `${fixed}simulations/sharedsimulations`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const isSharedLoading = isLoading;

  const fetchedSharedScenarios = data?.success ? data.data.simulations : [];
  return {
    fetchedSharedScenarios,
    isSharedLoading,
  };
};

/**
 * Fetch monthly volume breakdown for a simulation on demand (e.g. Excel export).
 * Used as a mutation rather than a query since it is triggered imperatively.
 */
export const useSimulationMonthlyVolume = () => {
  return useAuthenticatedMutation<any, string | number>(
    ['simulationMonthlyVolume'],
    (simulationId: string | number) => ({
      url: `${fixed}simulations/${simulationId}/monthly-volume`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
  );
};

export const GetArpprovedCountries = () => {
  const { data, isLoading } = useAuthenticatedQuery<ApprovedCountriesResponse>(
    ['approvedCountries'],
    {
      url: `${fixed}users/get_markets`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const isCountryLoading = isLoading;
  const approvedCountries = data?.success ? data.data.markets : [];
  return { approvedCountries, isCountryLoading };
};

export const GetAvailableCountries = () => {
  const { data, isLoading } = useAuthenticatedQuery<AvailableCountriesResponse>(
    ['availableCountries'],
    {
      url: `${fixed}approvals/countries/available`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const isCountryLoading = isLoading;
  const availableCountries =
    data?.success && Array.isArray(data.data)
      ? data.data.filter((c) => c.country_code !== 'global')
      : [];
  return { availableCountries, isCountryLoading };
};

export const GetSimulationNames = () => {
  const { data, isLoading } = useAuthenticatedQuery<SimulationNamesResponse>(['currentScenario'], {
    url: `${fixed}get_simulation_names`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  const SimulationName = data?.success ? data.data.simulations : [];
  return { SimulationName, isLoading };
};

export const GetApprovedUsers = () => {
  const { data, isLoading } = useAuthenticatedQuery<ApprovedUser>(['getApprovedUser'], {
    url: `${fixed}users/approved`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const isUserLoading = isLoading;
  const approvedUSers = data?.success ? data.data : {};
  return { approvedUSers, isUserLoading };
};

export const GetApprovalList = (
  excludeDevelopers = false,
  countries: string[] = [],
  roles: string[] = [],
  statuses: string[] = []
): { approved: AdminApprovedUser[]; isLoading: boolean } => {
  const selectedCountries = countries
    .filter((country) => country.toLowerCase() !== 'all')
    .map((country) => country.toLowerCase());
  const selectedRoles = roles.filter((role) => role.toLowerCase() !== 'all');
  const selectedStatuses = statuses.filter((status) => status.toLowerCase() !== 'all');
  const queryParams = new URLSearchParams({
    exclude_developers: String(excludeDevelopers),
  });

  selectedCountries.forEach((country) => queryParams.append('countries', country));
  selectedRoles.forEach((role) => queryParams.append('roles', role));
  selectedStatuses.forEach((status) => queryParams.append('statuses', status));

  const { data, isLoading } = useAuthenticatedQuery<AdminApprovedUser[]>(
    ['userList', excludeDevelopers, selectedCountries, selectedRoles, selectedStatuses],
    {
      url: `${fixed}users?${queryParams.toString()}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const approved: AdminApprovedUser[] = (data?.success && data.data) || [];
  return {
    approved,
    isLoading,
  };
};

export const GetPendingApproval = (): { pending: PendingApprovalData[]; isLoading: boolean } => {
  const { data, isLoading } = useAuthenticatedQuery<PendingApprovalData[]>(['pendingUsers'], {
    url: `${fixed}users/pending`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const pending: PendingApprovalData[] = (data?.success && data.data) || [];
  return {
    pending,
    isLoading,
  };
};
export const GetInnoExploreData = () => {
  const { data, isLoading } = useAuthenticatedQuery<any>(['inno-explore'], {
    url: `${fixed}inno-explore`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const innoExploreData = data?.success && data?.data ? data.data : [];

  return {
    innoExploreData,
    isLoading,
  };
};

export const GetAttributeMapData = () => {
  const { data, isLoading } = useAuthenticatedQuery<any>(['brand-attribute-mapping-approvals'], {
    url: `${fixed}brand-attribute-mapping/approvals`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const obbpcRequest = data?.success && data?.data ? data.data : [];

  return {
    obbpcRequest,
    isLoading,
  };
};

export const GetUser = (id: string): { user: AdminApprovedUser | null; isLoading: boolean } => {
  const location = useLocation();
  const { data, isLoading } = useAuthenticatedQuery<AdminApprovedUser>(
    ['EditUser', id, location.key],
    {
      url: `${fixed}user/${id}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const user: AdminApprovedUser | null = (data?.success && data.data) || null;

  return {
    user,
    isLoading,
  };
};

export const GetAdminDashboardOverview = (): {
  overview: DashboardOverviewResponse | null;
  isLoading: boolean;
} => {
  const { data, isLoading } = useAuthenticatedQuery<DashboardOverviewResponse>(
    ['adminDashboardOverview'],
    {
      url: `${fixed}admin/overview`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const overview: DashboardOverviewResponse | null = (data?.success && data.data) || null;

  return {
    overview,
    isLoading,
  };
};

export const logout = (msalDetails: IMsalContext) => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  const account = msalDetails.accounts[0]; // Select the first account
  msalDetails.instance
    .logoutRedirect({
      account: account,
    })
    .then((r) => console.debug(r))
    .catch((err) => console.error('Logout redirect failed:', err));
};

const fetchQuery = async (endpoint: string, data?: unknown, method: string = 'POST') => {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (method !== 'GET' && method !== 'HEAD') {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    toast.success('Request successful');
    return responseData;
  } catch (error) {
    const errorMessage = (error as Error).message;
    toast.error(`Request failed: ${errorMessage}`);
    throw error;
  }
};

// InnoImpact API
export interface InnoImpactFiltersResponse {
  countries: Array<{
    name: string;
    years: Array<{
      name: number;
      regions: Array<{
        name: string;
        innovation_types: Array<{
          name: string;
          sku_names: string[];
        }>;
      }>;
    }>;
  }>;
}

export const GetInnoImpactFilters = () => {
  const { data, isLoading } = useAuthenticatedQuery<InnoImpactFiltersResponse>(
    ['inno-impact-filters'],
    {
      url: `${fixed}inno-impact/get_filters_data`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  return { data, isLoading };
};

export interface InnoImpactFetchResultsPayload {
  country: string;
  region: string;
  sku_name: string;
  year: number;
  innovation_type: string;
}

// ─── Admin Simulation Endpoints ─────────────────────────────────────────────

/**
 * Get admin simulation stats (KPIs)
 */
export const useAdminSimulationStats = () => {
  return useAuthenticatedQuery<AdminSimulationStats>(['adminSimulationStats'], {
    url: `${fixed}admin/simulations/stats`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Get admin simulation filter options (markets, created_by, shared_by)
 */
export const useAdminSimulationFilterOptions = () => {
  return useAuthenticatedQuery<AdminSimulationFilterOptions>(['adminSimulationFilterOptions'], {
    url: `${fixed}admin/simulations/filter-options`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Get paginated list of admin simulations with filters
 */
export const useAdminSimulations = (params: AdminSimulationsParams) => {
  const queryString = new URLSearchParams(
    Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, String(value)])
  ).toString();

  const url = `${fixed}admin/simulations` + (queryString ? `?${queryString}` : '');

  return useAuthenticatedQuery<PaginatedScenariosResponse>(['adminSimulations', params], {
    url,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Get single admin simulation by ID
 */
export const useAdminSimulation = (simulationId: number | null) => {
  return useAuthenticatedQuery<AdminScenario>(
    ['adminSimulation', simulationId],
    {
      url: `${fixed}admin/simulations/${simulationId}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { enabled: !!simulationId } // Only fetch when simulationId exists
  );
};

/**
 * Delete admin simulation (soft delete)
 */
export const useDeleteAdminSimulation = () => {
  return useAuthenticatedMutation<SimulationMutationResponse, number>(
    ['deleteAdminSimulation'],
    (simulationId: number) => ({
      url: `${fixed}admin/simulations/${simulationId}`,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    })
  );
};

/**
 * Restore deleted admin simulation
 */
export const useRestoreAdminSimulation = () => {
  return useAuthenticatedMutation<SimulationMutationResponse, number>(
    ['restoreAdminSimulation'],
    (simulationId: number) => ({
      url: `${fixed}admin/simulations/${simulationId}/restore`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
  );
};

/**
 * Download admin simulation
 */
export const useDownloadAdminSimulation = () => {
  return useAuthenticatedMutation<Blob, number>(
    ['downloadAdminSimulation'],
    (simulationId: number) => ({
      url: `${fixed}admin/simulations/${simulationId}/download`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
    })
  );
};

/**
 * Export admin simulations to CSV
 */
export const useExportAdminSimulations = () => {
  return useAuthenticatedMutation<Blob, AdminSimulationsParams>(
    ['exportAdminSimulations'],
    (params: AdminSimulationsParams) => {
      const queryString = new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => [key, String(value)])
      ).toString();

      const url = `${fixed}admin/simulations/export` + (queryString ? `?${queryString}` : '');

      return {
        url,
        method: 'GET',
        headers: { 'Content-Type': 'text/csv' },
        responseType: 'blob',
      };
    }
  );
};

/**
 * Get admin simulation chart data
 */
export const useAdminSimulationCharts = (topUsersLimit: number = 10) => {
  return useAuthenticatedQuery<AdminSimulationCharts>(['adminSimulationCharts', topUsersLimit], {
    url: `${fixed}admin/simulations/charts?top_users_limit=${topUsersLimit}`,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export default fetchQuery;
